import { createSlice } from '@reduxjs/toolkit';

// Each undo step is a full copy of the site, so keep the history bounded.
const HISTORY_LIMIT = 60;

const pushHistory = (state) => {
    state.past.push(JSON.parse(JSON.stringify(state.present)));
    if (state.past.length > HISTORY_LIMIT) state.past.splice(0, state.past.length - HISTORY_LIMIT);
    state.lastEditKey = null;
};

const initialState = {
    past: [],
    present: {
        selectedTemplate: null,
        settings: {
        },
    },
    future: [],
    templatesdata: [],
    // Monotonic counter bumped whenever a WHOLE settings object is replaced
    // (AI full-generate apply / config seed) or a section is rewritten by AI.
    // The preview keys its rendered frame off this so that on Apply the section
    // components remount and re-seed their local `useState(reduxValue||default)`
    // fields from the freshly-applied Redux state. Without this the editable
    // fields (hero heading, about text, …) keep their first-mount value and the
    // AI result appears to "do nothing". It is deliberately NOT bumped by
    // per-field `updateSettings` so typing in an EditText never remounts.
    aiApplyNonce: 0,
    // Website editor: bumped per section when the properties panel changes it,
    // so only that section remounts and re-reads its settings.
    sectionVersions: {},
    // The field the last setField edited. Consecutive edits to the same field
    // (typing) share one undo step.
    lastEditKey: null,
};

const templatesSlice = createSlice({
    name: 'templates',
    initialState,
    reducers: {
        selectTemplatedata: (state, action) => {
            // `templatesdata` is external bulk data (prayer times, events, etc.)
            // fetched from the API — it is NOT part of the editable draft in
            // `present`. It must stay out of the undo/redo timeline, otherwise
            // every refetch would (a) wipe the redo stack via `future = []` and
            // (b) push a no-op snapshot of `present` that makes Undo appear dead.
            state.templatesdata = action.payload;
        },
        selectTemplate: (state, action) => {
            pushHistory(state);
            state.present.selectedTemplate = action.payload;
            state.future = [];
        },
        importSettings: (state, action) => {
            pushHistory(state);
            const { templateId, settings } = action.payload;
            state.present.settings[templateId] = settings;
            state.future = [];
            // Whole-object replace → force section components to re-seed local state.
            state.aiApplyNonce += 1;
        },
        updateSettings(state, action) {
            pushHistory(state);
            const { component, field, value } = action.payload;
            const selectedTemplate = state.present.settings[state.present.selectedTemplate];
            if (component && selectedTemplate[component]) {
                console.log('Entering the first condition');
                selectedTemplate[component][field] = value;
            
            } else if (!component) {
                selectedTemplate[field] = value;
            }
            state.future = [];
        },
        updatePrayerTime(state, action) {
            pushHistory(state);
            const { prayer, time, iqamah } = action.payload;

            const selectedTemplate = state.present.settings[state.present.selectedTemplate];
            if (!selectedTemplate || !selectedTemplate.prayerTimes || !selectedTemplate.prayerTimes.prayer[prayer]) {
                console.error(`Prayer time for ${prayer} does not exist`);
                return;
            }

            if (time) {
                selectedTemplate.prayerTimes.prayer[prayer].time = time;
            }
            if (iqamah) {
                selectedTemplate.prayerTimes.prayer[prayer].iqamah = iqamah;
            }
            state.future = [];
        },

        // Website editor: open a site (saved draft, published site or a fresh
        // template). Loading is not an edit, so the undo history starts empty.
        loadSite(state, action) {
            const { templateId, settings } = action.payload;
            state.present.selectedTemplate = templateId;
            state.present.settings[templateId] = settings;
            state.past = [];
            state.future = [];
            state.lastEditKey = null;
            state.sectionVersions = {};
            state.aiApplyNonce += 1;
        },
        // Website editor: set one field of a section (or a top-level setting
        // when section is null).
        setField(state, action) {
            const { section, field, value } = action.payload;
            const target = state.present.settings[state.present.selectedTemplate];
            if (!target) return;
            const editKey = `${section || ""}.${field}`;
            if (state.lastEditKey !== editKey) pushHistory(state);
            if (section) {
                target[section] = { ...(target[section] || {}), [field]: value };
                state.sectionVersions = state.sectionVersions || {};
                state.sectionVersions[section] = (state.sectionVersions[section] || 0) + 1;
            } else {
                // Top-level settings (colours, font) are read straight from the
                // store by the sections, so nothing needs to remount.
                target[field] = value;
            }
            state.lastEditKey = editKey;
            state.future = [];
        },
        undo(state) {
            state.lastEditKey = null;
            if (state.past.length > 0) {
                console.log('Undo - Past State:', state.past[state.past.length - 1]);
                console.log('Undo - Current State:', state.present);
                const previousState = state.past[state.past.length - 1];
                state.future.push(JSON.parse(JSON.stringify(state.present))); // Deep clone
                state.present = JSON.parse(JSON.stringify(previousState)); // Deep clone
                state.past.pop();
                // present was wholesale swapped → re-seed section local state.
                state.aiApplyNonce += 1;
            }
        },
        redo(state) {
            if (state.future.length > 0) {
                console.log('Redo - Future State:', state.future[state.future.length - 1]);
                console.log('Redo - Current State:', state.present);
                const nextState = state.future[state.future.length - 1];
                pushHistory(state);
                state.present = JSON.parse(JSON.stringify(nextState)); // Deep clone
                state.future.pop();
                // present was wholesale swapped → re-seed section local state.
                state.aiApplyNonce += 1;
            }
        },
        updateSelectedTemplateComponentArray: (state, action) => {
            pushHistory(state);
            const { componentKey, field, arrayData } = action.payload;
            const selectedTemplate = state.present.settings[state.present.selectedTemplate];
            if (selectedTemplate && selectedTemplate[componentKey]) {
                const component = selectedTemplate[componentKey];
                if (component[field] !== undefined) {
                    component[field] = arrayData;
                } else {
                    console.error(`Field ${field} not found in component ${componentKey}`);
                }
            } else {
                console.error(`Component ${componentKey} not found in selected template ${state.present.selectedTemplate}`);
            }
            state.future = [];
        },
        updateColorScheme(state, action) {
            pushHistory(state);
            const { colorScheme } = action.payload;
            const selectedColorScheme = state.present.settings[state.present.selectedTemplate].colorSchemes[colorScheme];
            state.present.settings[state.present.selectedTemplate].colorScheme = selectedColorScheme;
            state.future = [];
        },
        // Set colorScheme to a fully-resolved color OBJECT (used by the AI custom
        // theme + manual colour picker). Optionally stash the paired customTheme.
        setColorSchemeObject(state, action) {
            const target = state.present.settings[state.present.selectedTemplate];
            if (!target) return;
            const { colorScheme, customTheme } = action.payload;
            if (!colorScheme || typeof colorScheme !== "object") return;
            pushHistory(state);
            target.colorScheme = colorScheme;
            if (customTheme && typeof customTheme === "object") {
                target.customTheme = customTheme;
            }
            state.future = [];
        },
        updateFont(state, action) {
            pushHistory(state);
            const { font } = action.payload;
            state.present.settings[state.present.selectedTemplate].font = font;
            state.future = [];
        },
        setSectionOrder(state, action) {
            pushHistory(state);
            const { sectionOrder } = action.payload;
            state.present.settings[state.present.selectedTemplate].sectionOrder = sectionOrder;
            state.future = [];
        },
        toggleSectionVisibility(state, action) {
            pushHistory(state);
            const { section, visible } = action.payload;
            state.present.settings[state.present.selectedTemplate][section].show = visible;
            state.future = [];
        },
        updateLayoutDirection(state, action) {
            pushHistory(state);
            const { section, layoutDirection } = action.payload;
            state.present.settings[state.present.selectedTemplate][section].layoutDirection = layoutDirection;
            state.future = [];
        },
        addImageToSlider(state, action) {
            pushHistory(state);
            const { imageUrl } = action.payload;
            state.present.settings[state.present.selectedTemplate].supportUs.imageSlider.push(imageUrl);
            state.future = [];
        },
        updateCurrentImageIndex(state, action) {
            pushHistory(state);
            const { index } = action.payload;
            state.present.settings[state.present.selectedTemplate].supportUs.imageSlider.currentIndex = index;
            state.future = [];
        },
        setCurrentPage(state, action) {
            pushHistory(state);
            if (state.present.settings[state.present.selectedTemplate].isMultiPage) {
                state.present.currentPage = action.payload;
            }
            state.future = [];
        },

        updateButtonStyle(state, action) {
            pushHistory(state);
            const { buttonStyle } = action.payload;
            // const selectedbuttonStyle = state.present.settings[state.present.selectedTemplate].buttonStyle[buttonStyle];
            state.present.settings[state.present.selectedTemplate].selectedButtonStyle = buttonStyle;
            state.future = [];
        },
        // Replace one section's settings object in a single undoable step. Used
        // by the AI "rewrite section" flow so an entire AI rewrite is ONE ⌘Z
        // entry (instead of one per field). The `settings` value passed here is
        // already normalized by utils/ai/normalizeTemplate.js.
        mergeSectionSettings(state, action) {
            const { section, settings } = action.payload;
            const target = state.present.settings[state.present.selectedTemplate];
            if (!target || !section || !settings) return;
            pushHistory(state);
            target[section] = { ...target[section], ...settings };
            state.future = [];
            // Section replaced by AI → re-seed that section's local state.
            state.aiApplyNonce += 1;
        },
        // ---- Composable sections[] management (guide §8.5 #2/#3), all undoable ----
        reorderSections(state, action) {
            const { from, to } = action.payload;
            const target = state.present.settings[state.present.selectedTemplate];
            if (!target || !Array.isArray(target.sections)) return;
            const arr = target.sections;
            if (from < 0 || from >= arr.length || to < 0 || to >= arr.length) return;
            pushHistory(state);
            const [moved] = arr.splice(from, 1);
            arr.splice(to, 0, moved);
            state.future = [];
        },
        toggleSectionBlock(state, action) {
            const { id } = action.payload;
            const target = state.present.settings[state.present.selectedTemplate];
            if (!target || !Array.isArray(target.sections)) return;
            const block = target.sections.find((b) => b.id === id);
            if (!block) return;
            pushHistory(state);
            block.enabled = block.enabled === false ? true : false;
            state.future = [];
        },
        removeSectionBlock(state, action) {
            const { id } = action.payload;
            const target = state.present.settings[state.present.selectedTemplate];
            if (!target || !Array.isArray(target.sections)) return;
            pushHistory(state);
            target.sections = target.sections.filter((b) => b.id !== id);
            state.future = [];
        }
    },
});

export const {
    selectTemplate,
    loadSite,
    setField,
    updateSettings,
    updateColorScheme,
    setColorSchemeObject,
    updateFont,
    setSectionOrder,
    toggleSectionVisibility,
    updateLayoutDirection,
    updatePrayerTime,
    addImageToSlider,
    updateCurrentImageIndex,
    setCurrentPage,
    updateSelectedTemplateComponentArray,
    undo,
    redo,
    importSettings,
    selectTemplatedata,
    updateButtonStyle,
    mergeSectionSettings,
    reorderSections,
    toggleSectionBlock,
    removeSectionBlock
} = templatesSlice.actions;

// A change that leaves the site exactly as it was (a section re-saving the
// value it already has when it mounts) must not leave an undo step behind,
// or Undo lights up as soon as the editor opens.
const reducer = (state, action) => {
    const next = templatesSlice.reducer(state, action);
    if (
        state &&
        next.past.length === state.past.length + 1 &&
        JSON.stringify(next.past[next.past.length - 1]) === JSON.stringify(next.present)
    ) {
        return { ...next, past: state.past, future: state.future, lastEditKey: state.lastEditKey };
    }
    return next;
};

export default reducer;
